/**
 * TanStack Query keys. Invalidate by prefix: e.g. `qc.invalidateQueries({ queryKey: qk.listings(accountId) })`.
 * Every account-scoped key starts with ['acct', accountId] so switching accounts never shows stale data.
 */
import type { InboxFilter, ListingKind, Targeting } from './types';

export const qk = {
  me: ['me'] as const,
  myAccounts: ['myAccounts'] as const,
  isStaff: ['isStaff'] as const,
  policy: ['policy'] as const,
  attributeDefs: (kind: ListingKind) => ['attributeDefs', kind] as const,
  makes: (cls?: string | null) => ['makes', cls ?? 'all'] as const,
  models: (makeId: number) => ['models', makeId] as const,

  acct: (a: string) => ['acct', a] as const,
  listings: (a: string) => ['acct', a, 'listings'] as const,
  quote: (a: string, kind: ListingKind) => ['acct', a, 'quote', kind] as const,
  comps: (a: string, listingId: string) => ['acct', a, 'comps', listingId] as const,
  importRuns: (a: string) => ['acct', a, 'importRuns'] as const,
  feeds: (a: string) => ['acct', a, 'feeds'] as const,
  inbox: (a: string, f?: InboxFilter) => (f ? (['acct', a, 'inbox', f] as const) : (['acct', a, 'inbox'] as const)),
  thread: (a: string, leadId: string) => ['acct', a, 'thread', leadId] as const,
  savedReplies: (a: string) => ['acct', a, 'savedReplies'] as const,
  campaigns: (a: string) => ['acct', a, 'campaigns'] as const,
  campaign: (a: string, id: string) => ['acct', a, 'campaign', id] as const,
  audience: (t: Targeting) => ['audience', t] as const,
  campaignStats: (a: string, from: string, to: string) => ['acct', a, 'campaignStats', from, to] as const,
  daily: (a: string, from: string, to: string) => ['acct', a, 'daily', from, to] as const,
  billing: (a: string) => ['acct', a, 'billing'] as const,
  ledger: (a: string, from?: string, to?: string) => ['acct', a, 'ledger', from ?? '', to ?? ''] as const,
  cards: (a: string) => ['acct', a, 'cards'] as const,
  members: (a: string) => ['acct', a, 'members'] as const,
  settings: (a: string) => ['acct', a, 'settings'] as const,
  apiKeys: (a: string) => ['acct', a, 'apiKeys'] as const,
  webhooks: (a: string) => ['acct', a, 'webhooks'] as const,

  admin: ['admin'] as const,
  adminOverview: (from: string, to: string) => ['admin', 'overview', from, to] as const,
  adminPolicies: ['admin', 'policies'] as const,
  adminPrograms: ['admin', 'programs'] as const,
  adminGrants: ['admin', 'grants'] as const,
  adminAccounts: (q: string) => ['admin', 'accounts', q] as const,
  adminReviews: (f: { decision?: string; target_type?: string }) => ['admin', 'reviews', f] as const,
  adminAudit: (f: { action?: string }) => ['admin', 'audit', f] as const,
};
